import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import NavBar from "./NavBar";
import SkeletonCard from "./SkeletonCard";
import Footer from "./Footer";
import "../styles/home.css";
import { collection, getDocs, getFirestore } from "firebase/firestore";
import ProductImageSlideshow from "./ProductImageSlideshow";

const PRODUCTS_PER_PAGE = 24;

function Sale() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortOption, setSortOption] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const db = getFirestore();

  const { salepath } = useParams();
  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
  const collectionName = `sale${capitalize(salepath)}`;

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, collectionName));
        const allData = querySnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));

        // Set sort option from productFilter doc
        const filterDoc = allData.find((item) => item.id === "productFilter");
        if (filterDoc?.type) {
          setSortOption(filterDoc.type);
        }

        setData(allData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching collections: ", error);
      }
    };
    fetchCollections();
  }, [db, collectionName]);

  const handleSortChange = (e) => setSortOption(e.target.value);
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const filteredData = useMemo(() => {
    return [...data].filter(
      (item) => item.id !== "heading" && item.id !== "productFilter"
    );
  }, [data]);

  const headingText = useMemo(() => {
    const headingItem = data.find((item) => item.id === "heading");
    return headingItem ? headingItem.heading : "Sale";
  }, [data]);

  const sortedData = useMemo(() => {
    let sorted = [...filteredData];

    if (sortOption === "lowToHigh") {
      sorted.sort(
        (a, b) => parseFloat(a.productPrice) - parseFloat(b.productPrice)
      );
    } else if (sortOption === "highToLow") {
      sorted.sort(
        (a, b) => parseFloat(b.productPrice) - parseFloat(a.productPrice)
      );
    } else if (sortOption === "newest") {
      sorted.sort(
        (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
      );
    } else if (sortOption === "oldest") {
      sorted.sort(
        (a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)
      );
    }

    return sorted;
  }, [filteredData, sortOption]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return sortedData.slice(start, start + PRODUCTS_PER_PAGE);
  }, [sortedData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / PRODUCTS_PER_PAGE);

  return (
    <>
      <NavBar />
      <div className="mainContainer">
        <div className="new-arrivals container text-center mt-3 mb-5">
          <h1>{headingText}</h1>

          <div className="form-group text-start mt-4">
            <label htmlFor="sortOptions" className="form-label ms-2">
              Sort by:
            </label>
            <select
              id="sortOptions"
              className="form-select form-control bg-dark text-light border-0"
              value={sortOption}
              onChange={handleSortChange}
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="lowToHigh">Price, Low To High</option>
              <option value="highToLow">Price, High To Low</option>
            </select>
          </div>

          <div className="text-light text-end">
            Page {currentPage} of {totalPages}
          </div>

          <div className="row justify-content-center gx-2 mt-2">
            {loading
              ? Array.from({ length: 12 }).map((_, index) => (
                  <SkeletonCard key={index} />
                ))
              : paginatedData.map((item) => (
                  <div className="col-6 col-md-4 col-lg-3 mb-4" key={item.id}>
                    <div className="card border-0 p-2">
                      <Link
                        to={`/product/${collectionName}/${item.id}`}
                        state={{
                          product: item,
                          collectionName: collectionName,
                        }}
                        className="link"
                      >
                        <ProductImageSlideshow
                          item={item}
                          alt={item.productTitle}
                        />
                        <div className="card-body p-2">
                          {!item.inStock && (
                            <div className="soldOutContainer">
                              <h6 className="card-text text-center">
                                Sold Out
                              </h6>
                            </div>
                          )}
                          <h5 className="card-title text-start">
                            {item.productTitle}
                          </h5>
                          <div className="card-text text-start">
                            Rs {item.productPrice} PKR
                            {item.oldPrice && (
                              <div className="card-text text-start old_price">
                                Rs {item.oldPrice} PKR
                              </div>
                            )}
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>
                ))}
          </div>

          {/* Pagination */}
          <div className="pagination mt-4 d-flex justify-content-center">
            {Array.from({ length: totalPages }).map((_, i) => (
              <span
                key={i + 1}
                onClick={() => handlePageChange(i + 1)}
                className={`px-2 mx-1 cursor-pointer text-light ${
                  currentPage === i + 1
                    ? "border-bottom border-light"
                    : "opacity-50"
                }`}
                style={{ cursor: "pointer" }}
              >
                {i + 1}
              </span>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default Sale;
